//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import { createCommonStyles } from '../../../styles/commonStyles';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { formatDateForUI } from '../../../constants/FormatDate';
import * as ExpoDocumentPicker from "expo-document-picker";
import { Menu, MenuOptions, MenuOption, MenuTrigger, renderers } from 'react-native-popup-menu';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
//#endregion

const DocFormListView = React.memo(({ item, index, styles, t, isUser, isAccepted, handleEditPress, handleDelete, common,
    employeeOptions, calendarSelectedDate, calendarVisible, openCalendar, onCalendarDateChange,
    handleUploadDocuments, removeAttachment, downloadAttachment, openAttachment, CreateDivider
}) => {
    return (
        <Card key={item.rowguid} style={[common.cardstyle, { margin: 10 }]}>
            <Card.Content>
                <SafeAreaView style={{ flexDirection: "column" }}>
                    <SafeAreaView style={{ flexDirection: "row", flexWrap: 'nowrap', alignItems: 'center' }}>
                        <SafeAreaView style={styles.inputColumn}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <CustomInput
                                    value={formatDateForUI(item.dor)}
                                    setValue={() => { }}
                                    maxLen={50}
                                    editable={false}
                                    type="MAININPUT"
                                />
                                <Ionicons
                                    name={calendarVisible ? "calendar" : "calendar-outline"}
                                    style={styles.icons}
                                    onPress={!isAccepted && openCalendar}
                                />
                            </View>
                        </SafeAreaView>
                        <View style={{ width: '35%', flex: 1 }}>
                            <CustomInput
                                value={item.tdoc}
                                setValue={(e) => { !isAccepted && handleEditPress(index, item, 'tdoc', e) }}
                                maxLen={50}
                                type="MAININPUT"
                            />
                        </View>
                        <View style={{ width: '30%', flexDirection: 'row', alignItems: 'center' }}>
                            <CustomInput
                                value={item.fname}
                                setValue={() => { }}
                                maxLen={50}
                                editable={false}
                                type="MAININPUT"
                            />
                            <CustomButton
                                iconName='document-attach-outline'
                                onPress={() => !isAccepted && handleUploadDocuments(item, index)}
                                show='HOME_BELL'
                                type='HOME_BELL'
                                textTop='2'
                                sz={30} />
                            <View style={styles.attachmentIcons}>
                                <Menu renderer={renderers.SlideInMenu} style={styles.attachmentMenu}>
                                    <MenuTrigger>
                                        <Ionicons name="ellipsis-vertical" style={styles.dotsIcon} />
                                    </MenuTrigger>
                                    <MenuOptions customStyles={optionsStyles}>
                                        <MenuOption onSelect={() => openAttachment(item, index)} customStyles={cancelOptionStyles} text={t('common:open')} />
                                        {CreateDivider()}
                                        <MenuOption onSelect={() => downloadAttachment(item, index)} customStyles={cancelOptionStyles} text={t('common:downloadDocument')} />
                                        {CreateDivider()}
                                        {!isAccepted && <MenuOption onSelect={() => removeAttachment(item, index)} customStyles={removeOptionStyles} text={t('common:remove')} /> }
                                        {CreateDivider()}
                                        <MenuOption customStyles={cancelOptionStyles} text={t('common:cancel')} />
                                    </MenuOptions>
                                </Menu>
                            </View>
                        </View>
                        {!isUser && <View style={{ width: '35%', flex: 1 }}>
                            <CustomDropDown
                                type="EDIT_EMPLOYEE"
                                options={employeeOptions}
                                setValue={(val) => handleEditPress(index, item, 'reviewerid', val)}
                                value={item.reviewerid}
                                defValue={item.reviewerid}
                            />
                        </View>}
                        {!isAccepted && <CustomButton
                            iconName='trash-outline'
                            onPress={() => { handleDelete(item, index, 'rowguid') }}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={30} />}
                    </SafeAreaView>
                    <SafeAreaView style={{ alignItems: 'center' }}>
                        {calendarVisible && (
                            <CustomCalendar
                                calendarType={'RangePicker'}
                                setRange={(range) => onCalendarDateChange(range.startDate)}
                                range={{ startDate: calendarSelectedDate, endDate: calendarSelectedDate }}
                                allowSingleDate={true}
                                startingYear={1950}
                            />
                        )}
                    </SafeAreaView>
                </SafeAreaView>
            </Card.Content>
        </Card>
    );
});

const HRAdminDocFormScreen = (props) => {
    //#region Props
    const { isEdit, isUser, isAccepted, trnUI, employeeOptions, loadDocForm, saveDocForm, DeleteDocForm } = props
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();

    const [docFormData, setDocFormData] = useState([]);
    const [changes, setChanges] = useState({});
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);

    /* Calendar Related state variables */
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [calendarItemIndex, setCalendarItemIndex] = useState(null);
    const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);

    const openCalendar = (index, currentDate) => {
        setCalendarItemIndex(index);
        setCalendarVisible(true);
        setCalendarSelectedDate(currentDate || new Date().toISOString().split('T')[0]);
        setCalendarVisible(true);
    };

    const closeCalendar = () => {
        setCalendarVisible(false);
        setCalendarItemIndex(null);
        setCalendarSelectedDate(null);
    };

    const onCalendarDateChange = (newDate) => {
        if (calendarItemIndex !== null) {
            handleEditPress(calendarItemIndex, docFormData[calendarItemIndex], 'dor', newDate);
        }
        closeCalendar();
    };
    //#endregion

    useEffect(() => {
        setIsLoading(true);
        if (isEdit && trnUI) {
            loadDocForm(trnUI).then((mod) => {
                setDocFormData(mod);
                setRefreshAfterSave(false);
                setChanges({});
                setInsertData([]);
                setDeletedItems([]);
                setIsLoading(false);
            })
                .catch(() => {
                    setRefreshAfterSave(false);
                    setIsLoading(false);
                });
        }
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave, trnUI, isEdit])

    //#region Components
    const renderData = (item, index) => {
        return (
            <DocFormListView
                item={item}
                index={index}
                styles={styles}
                common={common}
                t={t}
                isUser={isUser}
                isAccepted={isAccepted}
                handleEditPress={handleEditPress}
                handleDelete={handleDelete}
                handleUploadDocuments={handleUploadDocuments}
                employeeOptions={employeeOptions}
                calendarSelectedDate={calendarSelectedDate}
                calendarVisible={calendarVisible && calendarItemIndex === index}
                openCalendar={() => openCalendar(index, item.dor)}
                onCalendarDateChange={onCalendarDateChange}
                removeAttachment={removeAttachment}
                downloadAttachment={downloadAttachment}
                openAttachment={openAttachment}
                CreateDivider={CreateDivider}
            />
        )
    };
    //#endregion

    //#region Attachments Handler
    // Handler to upload documents
    const handleUploadDocuments = useCallback(async (item, index) => {
        const maxSize = 30 * 1048576;
        let file = null;
        let uri = null;
        let type = null;
        let name = "";
        let ext = null;

        try {
            let result = await ExpoDocumentPicker.getDocumentAsync({
                type: [
                    "image/jpeg",
                    "image/gif",
                    "image/png",
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                ],
                copyToCacheDirectory: false,
                multiple: false,
            });

            if (!result.canceled) {
                file = result.output[0];
                type = result.assets[0].mimeType;
                uri = result.assets[0].uri.split(";base64,")[1];
                name = file.name;
                ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
            } else {
                return;
            };

            // if size allowed
            if (file.size < maxSize) {
                handleEditPress(index, item, 'fname', name);
                handleEditPress(index, item, 'tdoc', ext);
                handleEditPress(index, item, 'attachment', uri);
            } else {
                // file is too large
                showMessage({
                    message: t('common:errFile'),
                    type: "warning",
                    duration: 3000,
                });
            };
        } catch (error) {
            console.error(error);
        };
    }, [handleEditPress]);

    // Remove attachment handler
    const removeAttachment = useCallback((item, index) => {
        handleEditPress(index, item, 'fname', "");
        handleEditPress(index, item, 'attachment', "");
        handleEditPress(index, item, 'tdoc', "");

        showMessage({
            message: t('common:fileRemoved'),
            type: "info",
            duration: 2000,
        });
    }, [handleEditPress]);

    // Open Attachment handler
    const openAttachment = useCallback((item, index) => {
        const newWindow = window.open('', '_blank');
        if (!newWindow) {
            alert('Popup blocked. Please allow popups for this site.');
            return;
        };
        let base64Data = item.attachment;
        let type = item.tdoc.toLowerCase();
        let style = '';

        if (type === 'pdf') {
            type = 'application/pdf';
            style = 'width: 100%; height: 100%; display: block;';
        } else if (['png', 'jpg', 'jpeg', 'gif'].includes(type)) {
            type = `image/${type}`;
            style = 'display: block; max-width: 100%; height: auto;';
        } else {
            // fallback MIME type
            type = 'application/octet-stream';
            style = 'display: block; max-width: 100%; height: auto;';
        }

        const uri = `data:${type};base64,${base64Data}`;

        newWindow.document.write(`
                <html>
                <head>
                    <title>${item.fname || 'file_' + index}</title>
                    <style>
                    embed { ${style} }
                    </style>
                </head>
                <body style="margin:0">
                    <embed src="${uri}" type="${type}" />
                </body>
                </html>
            `);
        newWindow.document.close();
    }, [handleEditPress]);

    // Helper function to get the correct MIME type from the file extension
    const getMimeType = (extension) => {
        const ext = extension?.toLowerCase();
        switch (ext) {
            case 'pdf': return 'application/pdf';
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'png': return 'image/png';
            case 'gif': return 'image/gif';
            case 'doc': return 'application/msword';
            case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'xls': return 'application/vnd.ms-excel';
            case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            default: return 'application/octet-stream';
        }
    };

    const downloadAttachment = useCallback(async (item, index) => {
        // First, check if there is any file data to download.
        if (!item.attachment || item.attachment.trim() === '') {
            showMessage({
                message: t('common:noFileAvailable'),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        let downloadName = '';
        const fileName = item.fname;
        const fileType = item.tdoc?.toLowerCase();

        if (fileName?.toLowerCase().endsWith(`.${fileType}`)) {
            downloadName = fileName;
        } else {
            downloadName = `${fileName || 'file_' + index}.${fileType}`;
        }

        const link = document.createElement('a');
        let url;

        try {
            const mimeType = getMimeType(fileType);
            // Convert base64 string to a byte array
            const byteCharacters = atob(item.attachment);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create the Blob with the correct MIME type
            const blobData = new Blob([byteArray], { type: mimeType });

            // Trigger download 
            url = URL.createObjectURL(blobData);
            link.href = url;
            link.setAttribute('download', downloadName);

            // Append link to the body to ensure it's clickable in all browsers
            document.body.appendChild(link);

            // Start download
            link.click();

        } catch (error) {
            showMessage({
                message: t('common:failedDownload'),
                type: "danger",
                duration: 3000,
            });
        } finally {
            if (link.parentElement) {
                document.body.removeChild(link);
            }
            if (url) {
                URL.revokeObjectURL(url);
            }
        }
    }, []);


    const CreateDivider = () => {
        return (
            <View
                style={{
                    borderBottomColor: '#000000',
                    borderBottomWidth: StyleSheet.hairlineWidth,
                }}
            />
        );
    };
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, docFormData, setDocFormData)
    }, []);

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, docFormData, setDocFormData, setDeletedItems, ref)
    }

    // Update handleAddNew to add new module item with correct keys
    const handleAddNew = () => {
        let temp = [...docFormData];
        if (docFormData.length === 0)
            temp = {
                rowguid: generateUniqueID(),
                trnUI: trnUI,
                dor: "",
                tdoc: "",
                reviewerid: "",
                fname: ""
            }
        rowAdd(temp, setDocFormData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
        handleEditPress()
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setDeletedItems([])
    }

    //#region SaveAttachments
    const handleSave = async () => {
        if (Object.entries(changes).length === 0 && deletedItems.length === 0 && insertData.length === 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        // Validate required fields
        for (const mod of docFormData) {
            if (!mod.dor) {
                showMessage({
                    message: t('common:dateEmpty'),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
            if (!mod.tdoc) {
                showMessage({
                    message: t('common:typeEmpty'),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
        }

        setIsLoading(true);

        // Prepare data for insert and update
        const dataForInsert = [];
        const dataForUpdate = [];
        const insertSuccess = true;
        const updateSuccess = true;
        const deleteSuccess = true;
        const promises = [];

        docFormData.forEach(mod => {
            if (insertData.includes(mod.rowguid)) {
                dataForInsert.push(mod);
            } else {
                dataForUpdate.push(mod);
            }
        });

        dataForInsert.forEach(item => {
            item.trnUI = trnUI;
            item.rowguid = item.rowguid || generateUniqueID();
        });

        dataForUpdate.forEach(item => {
            item.trnUI = trnUI;
        });

        if (dataForInsert.length > 0) {
            promises.push(
                saveDocForm(dataForInsert)
                    .then(res => {
                        if (res !== 200)
                            insertSuccess = false;
                    })
            );
        }

        if (dataForUpdate.length > 0) {
            promises.push(
                saveDocForm(dataForUpdate)
                    .then(res => {
                        if (res !== 200)
                            updateSuccess = false;
                    })
            );
        }

        if (deletedItems.length > 0) {
            promises.push(
                DeleteDocForm(deletedItems)
                    .then(res => {
                        if (res !== 200)
                            deleteSuccess = false;
                    })
            );
        }

        try {
            await Promise.all(promises);
            if (updateSuccess && insertSuccess && deleteSuccess) {
                showMessage({
                    message: t('common:allOperationsSuccMess'),
                    type: "success",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            } else {
                showMessage({
                    message: t('common:saveFailed'),
                    type: "danger",
                    duration: 3000,
                });
                setRefreshAfterSave(true);
                removeFromChangesAfterSave();
            }
        } catch (error) {
            showMessage({
                message: t('common:saveFailed'),
                type: "danger",
                duration: 3000,
            });
            setRefreshAfterSave(true);
            removeFromChangesAfterSave();
        } finally {
            setIsLoading(false);
        }
    };
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.innerView}>
            <SafeAreaView style={[styles.buttons, styles.functionalityButtons]}>
                {!isAccepted && <>
                <CustomButton
                    text={t('common:new')}
                    onPress={() => { handleAddNew() }}
                    type='BUTTON_ADD'
                    textTop='0'
                    sz={15}
                />
                <CustomButton
                    text={t('common:save')}
                    onPress={() => { handleSave() }}
                    type='BUTTON_SAVE'
                    textTop='0'
                    sz={15}
                    isGradient={true}
                />
                <CustomButton
                    text={t('common:cancel')}
                    onPress={() => { setRefreshAfterSave(true) }}
                    type="BUTTON_CANCEL"
                    textTop='0'
                    sz={15}
                /></>}
            </SafeAreaView>
            <FlatList
                data={docFormData}
                keyExtractor={(item) => item.rowguid}
                scrollEnabled={true}
                initialNumToRender={4}
                renderItem={({ item, index }) =>
                    renderData(item, index)
                }
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={[styles.rowHeaders]}>
                            <CustomLabel
                                value={t('common:date')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:documentType')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:fileName')}
                                type='SUBTITLE' />
                            {!isUser && <CustomLabel
                                value={t('common:reviewer')}
                                type='SUBTITLE' />}
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel
                            value={t("common:NoDataPreview")}
                            type="NO_FUNCTIONS"
                        />
                }
            />
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    innerView: {
        flex: 1,
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
    listTitle: {
        flex: 1,
        marginVertical: 10,
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
    attachmentIcons: {
        width: 20,
        alignSelf: 'center',
        color: colors.text_primary,
    },
    icons: {
        fontSize: 25,
        color: colors.text_primary,
        margin: '5%'
    },
});
const optionsStyles = {
    optionsContainer: {
        marginHorizontal: '3.5%',
        marginVertical: '-3%',
        borderRadius: 10,
    },
    optionWrapper: {
        height: 50,
        justifyContent: 'center',
    },
};
const removeOptionStyles = {
    optionText: {
        textAlign: 'center',
        fontSize: 16,
        color: '#dd3636',
    },
};
const cancelOptionStyles = {
    optionText: {
        textAlign: 'center',
        fontSize: 16,
    },
};
//#endregion

export default HRAdminDocFormScreen;